/**
 * Auth redirect helpers – canonical "intended destination" resolution.
 *
 * QA checklist:
 *  1) /app/producer/... → guard → /login → login → back to /app/producer/...
 *  2) Same with Google OAuth
 *  3) Forgot password from /app/producer/... → reset → back
 *  4) Direct /login when logged in → /app/producer (host) or /explore (visitor)
 *  5) next=https://evil.com → ignored, fallback used
 */

/**
 * Canonical public site URL for email redirects.
 * Uses VITE_PUBLIC_SITE_URL env var, falls back to window.location.origin.
 */
export function getPublicSiteUrl(): string {
  if (typeof import.meta !== 'undefined') {
    const env = (import.meta as any).env?.VITE_PUBLIC_SITE_URL;
    if (env && typeof env === 'string' && env.length > 0) return env.replace(/\/+$/, '');
  }
  return window.location.origin;
}

/**
 * Validate and sanitise a redirect path.
 * Blocks open redirects – only relative paths starting with "/" are allowed.
 * Returns null if the path is invalid.
 */
// export function safeNextPath(raw: string | null | undefined): string | null {
//   if (!raw || typeof raw !== 'string') return null;
//   const trimmed = raw.trim();
//   if (!trimmed.startsWith('/')) return null;
//   if (trimmed.startsWith('//')) return null;
//   if (/https?:/i.test(trimmed)) return null;
//   return trimmed;
// }

export function safeNextPath(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== 'string') return null;

  try {
    const url = new URL(raw, window.location.origin);

   const isDev = import.meta.env.DEV;

    const allowedOrigins = [
      window.location.origin,
      ...(isDev ? ['http://localhost:8080'] : []),
    ];

    if (!allowedOrigins.includes(url.origin)) return null;

    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}


interface ResolveNextPathOpts {
  /** React Router location.state.from (set by AuthRouteGuard) */
  fromState?: { pathname?: string; search?: string; hash?: string } | null;
  /** window.location.search – we extract ?next= from it */
  search?: string;
  /** Whether the current user is a host/producer member */
  isHost?: boolean;
}

/**
 * Resolve the intended destination after auth, in priority order:
 *  1) fromState (set by AuthRouteGuard via Navigate state)
 *  2) ?next= query parameter
 *  3) Smart fallback: /app/producer (host) or /explore (visitor)
 */
export function resolveNextPath(opts: ResolveNextPathOpts = {}): string {
  const { fromState, search, isHost } = opts;

  // 1) fromState from AuthRouteGuard
  if (fromState?.pathname) {
    const safe = safeNextPath(
      fromState.pathname + (fromState.search ?? '') + (fromState.hash ?? ''),
    );
    if (safe) return safe;
  }

  // 2) ?redirectTo= or ?next= query param (redirectTo is canonical, next is legacy)
  if (search) {
    const params = new URLSearchParams(search);
    const candidates = ['redirectTo', 'next', 'redirect', 'returnTo'];
    for (const key of candidates) {
      const val = params.get(key);
      const safe = safeNextPath(val);
      if (safe) return safe;
    }
  }

  // 3) Smart fallback
  return isHost ? '/app/producer' : '/explore';
}
